import { DatePicker } from "@/components/generic/DatePicker";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { useMemo } from "react";
import { DateValue } from "react-aria";
import {
  Control,
  FieldPathByValue,
  FieldValues,
  useController,
  UseFormTrigger,
} from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../../basic/SegmentHeader";

export const campaignPeriodFieldValueSchema = z
  .object({
    startDate: z.iso.date({
      error: (issue) =>
        issue.code === "invalid_type"
          ? "Du skal vælge en startdato"
          : issue.message,
    }),
    endDate: z.iso.date().nullable(),
  })
  .superRefine((data, ctx) => {
    if (!data.endDate) return;
    const startDate = parseDate(data.startDate);
    const endDate = parseDate(data.endDate);
    if (endDate.compare(startDate) < 1)
      ctx.addIssue({
        code: "custom",
        path: ["endDate"],
        message: "Skal være mindst 1 dag efter startdato",
      });
  });

export type CampaignPeriodFieldValue = z.infer<
  typeof campaignPeriodFieldValueSchema
>;

export function CampaignPeriodField<T extends FieldValues>({
  control,
  name,
  trigger,
}: {
  control: Control<T>;
  name: FieldPathByValue<T, CampaignPeriodFieldValue>;
  trigger: UseFormTrigger<T>;
}) {
  const startDateName = `${name}.startDate` as FieldPathByValue<
    T,
    CampaignPeriodFieldValue["startDate"]
  >;
  const endDateName = `${name}.endDate` as FieldPathByValue<
    T,
    CampaignPeriodFieldValue["endDate"]
  >;

  const startDateController = useController({
    control,
    name: startDateName,
  });

  const endDateController = useController({
    control,
    name: endDateName,
  });

  const defaultStartDate = useMemo(() => {
    const value = startDateController.formState.defaultValues?.[name]
      ?.startDate as CampaignPeriodFieldValue["startDate"] | undefined;
    return value ? parseDate(value) : null;
  }, [startDateController.formState.defaultValues, name]);

  const defaultEndDate = useMemo(() => {
    const value = endDateController.formState.defaultValues?.[name]?.endDate as
      | CampaignPeriodFieldValue["endDate"]
      | undefined;
    return value ? parseDate(value) : null;
  }, [endDateController.formState.defaultValues, name]);

  const startDate = useMemo(() => {
    const value = startDateController.field
      .value as CampaignPeriodFieldValue["startDate"];
    return value ? parseDate(value) : null;
  }, [startDateController.field.value]);

  const endDate = useMemo(() => {
    const value = endDateController.field
      .value as CampaignPeriodFieldValue["endDate"];
    return value ? parseDate(value) : null;
  }, [endDateController.field.value]);

  const currentDate = useMemo(() => today(getLocalTimeZone()), []);

  const minStartDate = useMemo(() => {
    if (defaultStartDate && defaultStartDate.compare(currentDate) < 0)
      return defaultStartDate;
    return currentDate;
  }, [currentDate, defaultStartDate]);

  const minEndDate = useMemo(() => {
    const baseDate = startDate ?? minStartDate;
    return baseDate.add({ days: 1 });
  }, [minStartDate, startDate]);

  const isDateUnavailable = (
    date: DateValue,
    defaultValue?: DateValue | null,
  ) => {
    if (defaultValue && date.compare(defaultValue) === 0) return false;
    if (date.compare(currentDate) < 0) return true;
    return false;
  };

  return (
    <div className="flex w-full flex-col items-center gap-y-10">
      <SegmentHeader
        className="max-w-lg"
        title="Periode"
        description="Sæt start- og slutdato for kampagnen. Kampagner uden slutdato vil køre indtil, der ikke er flere leads."
      />

      <div className="flex w-full max-w-160 gap-x-4 *:w-full *:shrink">
        <DatePicker
          aria-label="Startdato"
          placeholder="Vælg startdato"
          onChange={(date) => {
            startDateController.field.onChange(date?.toString() ?? null);
            if (
              endDateController.fieldState.invalid ||
              startDateController.formState.isSubmitted
            )
              trigger(endDateName);
          }}
          minValue={minStartDate}
          isDateUnavailable={(date) =>
            isDateUnavailable(date, defaultStartDate)
          }
          value={startDate}
          onBlur={startDateController.field.onBlur}
          errorMessage={startDateController.fieldState.error?.message}
        />

        <DatePicker
          aria-label="Slutdato"
          placeholder="Vælg slutdato"
          onChange={(date) => {
            endDateController.field.onChange(date?.toString() ?? null);
          }}
          minValue={minEndDate}
          isDateUnavailable={(date) => isDateUnavailable(date, defaultEndDate)}
          value={endDate}
          onBlur={endDateController.field.onBlur}
          errorMessage={endDateController.fieldState.error?.message}
        />
      </div>
    </div>
  );
}
